import React, {
	useCallback,
	type MouseEvent,
	type KeyboardEvent,
	forwardRef,
	type PropsWithChildren,
} from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Anchor, xcss } from '@atlaskit/primitives';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	fireUIAnalytics,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { Link, type LinkProps } from '@atlassian/react-resource-router';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../controllers/features/selectors.tsx';

type DiscreteProps = LinkProps & {
	innerFocusOutline?: boolean;
	shouldDisplayStrikethrough?: boolean;
	isSubtle?: boolean;
	'aria-label'?: string;
	ref?: React.Ref<HTMLAnchorElement>;
};

export const TableCellDiscreteLink = forwardRef<HTMLAnchorElement, DiscreteProps>(
	({ onClick, ...rest }, ref) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
		const onClickWithAnalytics = useCallback(
			(e: MouseEvent | KeyboardEvent) => {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'link',
					}),
					'tableIssueLink',
				);
				onClick && onClick(e);
			},
			[createAnalyticsEvent, onClick],
		);

		const isEmbedMode = useIsEmbedMode();

		return (
			<StyledLink
				{...rest}
				onClick={onClickWithAnalytics}
				ref={ref}
				isSingleLineRowHeightEnabled={isSingleLineRowHeightEnabled}
				{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
			/>
		);
	},
);

type Props = PropsWithChildren<{
	'aria-label'?: string;
	href: string;
	isTruncated?: boolean;
	onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
	shouldDisplayStrikethrough?: boolean;
	testId?: string;
}>;

export const TableCellLink = forwardRef<HTMLAnchorElement, Props>(
	(
		{
			'aria-label': ariaLabel,
			children,
			href,
			isTruncated,
			onClick,
			shouldDisplayStrikethrough,
			testId,
		},
		ref,
	) => {
		const onClickWithAnalytics = useCallback(
			(e: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'tableIssueLink');
				onClick && onClick(e);
			},
			[onClick],
		);

		const isEmbedMode = useIsEmbedMode();

		return (
			<Anchor
				aria-label={ariaLabel}
				href={href}
				onClick={onClickWithAnalytics}
				ref={ref}
				testId={testId}
				xcss={[
					linkDefaultStyles,
					isTruncated && linkTruncatedStyles,
					shouldDisplayStrikethrough && linkStrikethroughStyles,
				]}
				{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
			>
				{children}
			</Anchor>
		);
	},
);

// Fix for Compiled bug where invalid props are passed on to the underlying DOM element when using inheritance.
const PropFilteredLink = forwardRef<HTMLAnchorElement, DiscreteProps>(
	({ innerFocusOutline, shouldDisplayStrikethrough, isSubtle, ...rest }, ref) => (
		// eslint-disable-next-line @atlassian/a11y/anchor-has-content -- See https://go/a11y-anchor-has-content for more details
		<Link {...rest} ref={ref} />
	),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLinkOld = styled(PropFilteredLink)<{
	innerFocusOutline?: boolean;
	shouldDisplayStrikethrough?: boolean;
	isSubtle?: boolean;
	isSingleLineRowHeightEnabled?: boolean;
}>(
	{
		wordBreak: 'break-word',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ innerFocusOutline }) =>
		innerFocusOutline && {
			'&:focus-visible': {
				outlineOffset: token('space.negative.025'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-1876
	({ shouldDisplayStrikethrough }) =>
		shouldDisplayStrikethrough && {
			'text-decoration': 'line-through',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-1876
	({ isSingleLineRowHeightEnabled }) =>
		isSingleLineRowHeightEnabled && {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			display: 'block',
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLinkNew = styled(PropFilteredLink)<{
	innerFocusOutline?: boolean;
	shouldDisplayStrikethrough?: boolean;
	isSubtle?: boolean;
	isSingleLineRowHeightEnabled?: boolean;
}>(
	{
		wordBreak: 'break-word',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
		color: ({ isSubtle }) => (isSubtle ? token('color.text.subtle') : token('color.text')),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ innerFocusOutline }) =>
		innerFocusOutline && {
			'&:focus-visible': {
				outlineOffset: token('space.negative.025'),
			},
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-1876
	({ shouldDisplayStrikethrough }) =>
		shouldDisplayStrikethrough && {
			'text-decoration': 'line-through',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-1876
	({ isSingleLineRowHeightEnabled }) =>
		isSingleLineRowHeightEnabled && {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			display: 'block',
		},
);

const linkDefaultStyles = xcss({
	cursor: 'pointer',
	display: 'block',
	font: 'font.body',
	overflow: 'hidden',
	textDecoration: 'underline',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});
const linkTruncatedStyles = xcss({
	whiteSpace: 'nowrap',
});
const linkStrikethroughStyles = xcss({
	textDecoration: 'line-through',
	':hover': {
		textDecoration: 'line-through',
	},
});

const StyledLink = componentWithCondition(isVisualRefreshEnabled, StyledLinkNew, StyledLinkOld);
